import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";

/**
 * A pure-keyboard date input that shows DD/MM/YYYY and stores YYYY-MM-DD.
 *
 * Why we're not just using `<Input type="date" />`:
 * On tablets the native date picker cannot be dismissed by the numeric
 * keyboard, forcing operators to two-hand a calendar wheel. Wholesale
 * bakery best-before dates are always typed off the supplier label —
 * hence a masked numeric text input is much faster.
 *
 * Props:
 *   value      YYYY-MM-DD string (persistent shape backend expects).
 *   onChange   fn(nextValue) — called with '' when the input is cleared,
 *              a YYYY-MM-DD when a fully valid date is typed, and '' also
 *              when the operator is mid-typing / entered nonsense (parent
 *              can rely on `!!value` == "have a valid date").
 *   dataTestid Optional — passed straight to the <input>.
 *   allowPast  Default true. Pass false to reject dates in the past.
 *   maxYear    Default 2100 (sanity guard).
 *   className  Passed through to the input.
 *
 * The mask lets the operator type 8 raw digits (DDMMYYYY) and inserts the
 * `/` separators; backspace/delete work at any position; paste of a full
 * date string is respected.
 */
export default function DateInput({
  value,
  onChange,
  dataTestid,
  className = "",
  allowPast = true,
  maxYear = 2100,
  ...rest
}) {
  const [display, setDisplay] = useState(() => isoToDisplay(value));
  const [error, setError] = useState("");

  // Keep local display in sync when the parent's value changes externally
  // (e.g. form reset after save).
  useEffect(() => {
    setDisplay((prev) => {
      const asIso = displayToIso(prev);
      if (asIso === value) return prev; // don't clobber the operator's typing
      return isoToDisplay(value);
    });
    setError("");
  }, [value]);

  const handleChange = (e) => {
    const raw = e.target.value;
    const digits = raw.replace(/\D/g, "").slice(0, 8);
    const masked = maskDate(digits);
    setDisplay(masked);
    setError("");
    const iso = digits.length === 8 ? digitsToIso(digits) : "";
    if (iso) {
      const err = validateIso(iso, { allowPast, maxYear });
      if (err) {
        setError(err);
        onChange("");
      } else {
        onChange(iso);
      }
    } else if (raw === "") {
      onChange("");
    } else {
      // Partial input — parent shouldn't have a value yet.
      onChange("");
    }
  };

  const showErr = !!error && display.length > 0;

  return (
    <>
      <Input
        type="text"
        inputMode="numeric"
        pattern="\d{2}/\d{2}/\d{4}"
        maxLength={10}
        placeholder="DD/MM/YYYY"
        value={display}
        onChange={handleChange}
        data-testid={dataTestid}
        aria-invalid={showErr ? "true" : "false"}
        className={`${className} ${showErr ? "border-rose-400 focus-visible:ring-rose-400" : ""}`}
        {...rest}
      />
      {showErr && (
        <div
          className="text-xs text-rose-600 mt-1"
          data-testid={dataTestid ? `${dataTestid}-error` : undefined}
        >
          {error}
        </div>
      )}
    </>
  );
}

// ----------------------------- helpers ------------------------------------

function isoToDisplay(iso) {
  if (!iso) return "";
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return "";
  return `${m[3]}/${m[2]}/${m[1]}`;
}

function displayToIso(disp) {
  if (!disp) return "";
  const m = String(disp).match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return "";
  return `${m[3]}-${m[2]}-${m[1]}`;
}

function maskDate(digits) {
  const d = digits.slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
}

function digitsToIso(digits) {
  const dd = digits.slice(0, 2);
  const mm = digits.slice(2, 4);
  const yyyy = digits.slice(4, 8);
  return `${yyyy}-${mm}-${dd}`;
}

function validateIso(iso, { allowPast, maxYear }) {
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return "Invalid date";
  if (y < 1900 || y > maxYear) return `Year must be 1900–${maxYear}`;
  if (m < 1 || m > 12) return "Month must be 1–12";
  if (d < 1 || d > 31) return "Day must be 1–31";
  // Real calendar validation (catches 31 Feb, etc).
  const asDate = new Date(iso + "T00:00:00Z");
  if (
    asDate.getUTCFullYear() !== y ||
    asDate.getUTCMonth() + 1 !== m ||
    asDate.getUTCDate() !== d
  ) {
    return "Not a real date";
  }
  if (!allowPast) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (asDate < today) return "Date is in the past";
  }
  return "";
}

// Re-export helpers in case other pages want the parsing without the input.
export { isoToDisplay, displayToIso };
